import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Heart, 
  Home,
  Utensils,
  Shirt,
  Bath,
  Sparkles,
  Film,
  Gamepad2,
  Bed,
  Stethoscope,
  Clock,
  Shield,
  Users,
  CheckCircle2,
  Phone,
  ArrowRight,
  Star,
  Play,
  Download,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  {
    icon: Utensils,
    title: "Nutritious Meals",
    description: "Three chef-prepared meals daily plus snacks, tailored to dietary needs and preferences.",
    color: "#00f0ff"
  },
  {
    icon: Shirt,
    title: "Laundry Services",
    description: "Full laundry and linen services including washing, drying, folding, and wardrobe organization.",
    color: "#ff00ff"
  },
  {
    icon: Bath,
    title: "Personal Care",
    description: "Assistance with bathing, grooming, dressing, and all personal hygiene needs with dignity and respect.",
    color: "#ff9e00"
  },
  {
    icon: Sparkles,
    title: "Spa & Wellness",
    description: "Regular spa visits, therapeutic massages, manicures, pedicures, and relaxation treatments.",
    color: "#00f0ff"
  },
  {
    icon: Film,
    title: "Movie Nights",
    description: "Weekly movie screenings in our theater room with popcorn, comfortable seating, and classic films.",
    color: "#ff00ff"
  },
  {
    icon: Gamepad2,
    title: "Game Days",
    description: "Daily activities including card games, bingo, puzzles, trivia, and social gatherings.",
    color: "#ff9e00"
  },
  {
    icon: Stethoscope,
    title: "24/7 Medical Care",
    description: "Round-the-clock nursing staff, medication management, and on-call physicians.",
    color: "#00f0ff"
  },
  {
    icon: Heart,
    title: "Hospice Care",
    description: "Compassionate end-of-life care services with focus on comfort, dignity, and family support.",
    color: "#ff00ff"
  }
];

const amenities = [
  "125 Private & Semi-Private Rooms",
  "State-of-the-Art Medical Equipment",
  "Beautiful Garden & Outdoor Spaces",
  "On-Site Physical Therapy",
  "Chapel & Spiritual Services",
  "Family Visiting Lounges",
  "Hair Salon & Barber Shop",
  "Library & Reading Room",
  "Activity & Craft Rooms",
  "Secure Memory Care Unit",
  "Emergency Response Systems",
  "Wheelchair Accessible Throughout"
];

export default function SeniorLivingPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error("Please fill in required fields");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        subject: "Senior Living Inquiry"
      });
      toast.success("Thank you! Our team will contact you within 24 hours.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to submit. Please call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="senior-living-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 miami-grid relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#ff00ff]/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#00f0ff]/20 rounded-full blur-[150px]" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-[#1a0b2e] border border-[#4b0082] rounded-full text-[#ff00ff] text-sm font-mono uppercase tracking-widest mb-6">
                Senior Resident Living
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
                A Place to Call <span className="text-gradient">Home</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Experience exceptional senior living with 24/7 care, engaging activities, and a warm community. 
                We welcome all residents, including those requiring hospice care services.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-[#1a0b2e]/80 border border-[#4b0082] rounded-full px-4 py-2">
                  <Bed className="w-5 h-5 text-[#00f0ff]" />
                  <span className="text-white font-semibold">125 Beds Available</span>
                </div>
                <div className="flex items-center gap-2 bg-[#1a0b2e]/80 border border-[#4b0082] rounded-full px-4 py-2">
                  <Clock className="w-5 h-5 text-[#ff00ff]" />
                  <span className="text-white font-semibold">24/7 Care</span>
                </div>
                <div className="flex items-center gap-2 bg-[#1a0b2e]/80 border border-[#4b0082] rounded-full px-4 py-2">
                  <Heart className="w-5 h-5 text-[#ff9e00]" />
                  <span className="text-white font-semibold">Hospice Welcome</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <a href="tel:8449436175">
                  <Button 
                    data-testid="senior-call-btn"
                    className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-8 py-6 hover:bg-[#00c0cc] hover:shadow-[0_0_20px_rgba(0,240,255,0.6)] transition-all"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call 844-943-6175
                  </Button>
                </a>
                <Button 
                  data-testid="senior-tour-btn"
                  variant="outline"
                  className="border-2 border-[#ff00ff] text-[#ff00ff] font-bold uppercase tracking-wider rounded-full px-8 py-6 hover:bg-[#ff00ff] hover:text-white transition-all"
                  onClick={() => document.getElementById('inquiry-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Schedule a Tour
                </Button>
              </div>
              
              {/* Download Brochure Button */}
              <div className="mt-6">
                <a 
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/brochure`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    data-testid="download-brochure"
                    variant="ghost"
                    className="text-gray-300 hover:text-[#00f0ff] hover:bg-[#00f0ff]/10"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download Brochure (PDF)
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1773227059881-ef8ecf22aac8?w=600"
                alt="Seniors enjoying community"
                className="rounded-3xl w-full h-[450px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#1a0b2e] border border-[#4b0082] rounded-2xl p-5 glass">
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {[1,2,3].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00f0ff] to-[#ff00ff] border-2 border-[#0f0518]" />
                    ))}
                  </div>
                  <div>
                    <div className="text-white font-semibold">Join Our Family</div>
                    <div className="text-gray-400 text-sm">100+ Happy Residents</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 bg-[#1a0b2e]/30 border-y border-[#4b0082]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "125", label: "Private Beds", color: "#00f0ff" },
              { value: "24/7", label: "Nursing Care", color: "#ff00ff" },
              { value: "20+", label: "Years Experience", color: "#ff9e00" },
              { value: "100%", label: "Care Commitment", color: "#00f0ff" },
            ].map((stat, index) => (
              <div key={index}>
                <div className="text-4xl md:text-5xl font-bold mb-2" style={{ color: stat.color }}>
                  {stat.value}
                </div>
                <div className="text-gray-400 text-sm uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Our Services</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              Complete Care for Every Need
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We take care of all daily needs so residents can focus on enjoying life. From personal care to entertainment, we've got everything covered.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-${index}`}
                className="glass rounded-2xl p-6 card-hover"
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: `${service.color}15` }}
                >
                  <service.icon className="w-7 h-7" style={{ color: service.color }} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Virtual Tour Section */}
      <section className="py-20 lg:py-28 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#00f0ff] text-sm font-mono uppercase tracking-widest">Take a Look</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              Virtual Tour
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Explore our beautiful facility from the comfort of your home
            </p>
          </div>

          <div className="relative max-w-4xl mx-auto">
            {/* Video Container */}
            <div className="relative rounded-3xl overflow-hidden aspect-video bg-[#0f0518] border border-[#4b0082]">
              {/* Placeholder/Thumbnail with Play Button */}
              <img
                src="https://images.pexels.com/photos/7545214/pexels-photo-7545214.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Senior Living Virtual Tour"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <a 
                  href="https://www.youtube.com/watch?v=dQw4w9WgXcQ" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  data-testid="virtual-tour-play"
                  className="w-24 h-24 rounded-full bg-[#00f0ff] flex items-center justify-center hover:scale-110 hover:shadow-[0_0_40px_rgba(0,240,255,0.6)] transition-all cursor-pointer group"
                >
                  <Play className="w-10 h-10 text-black ml-1 group-hover:scale-110 transition-transform" />
                </a>
              </div>
              
              {/* Video Info Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <h3 className="text-white font-semibold text-lg">Welcome to HealthCentrix Senior Living</h3>
                <p className="text-gray-300 text-sm">Take a virtual walkthrough of our facility, meet our staff, and see what daily life looks like.</p>
              </div>
            </div>

            {/* Tour Highlights */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              {[
                { label: "Private Rooms", icon: Home },
                { label: "Dining Hall", icon: Utensils },
                { label: "Activity Center", icon: Gamepad2 },
                { label: "Gardens", icon: Sparkles },
              ].map((item, index) => (
                <div 
                  key={index}
                  className="glass rounded-xl p-4 text-center hover:border-[#00f0ff]/50 transition-colors"
                >
                  <item.icon className="w-6 h-6 text-[#00f0ff] mx-auto mb-2" />
                  <span className="text-white text-sm font-medium">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Activities Gallery */}
      <section className="py-20 lg:py-28 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00f0ff] text-sm font-mono uppercase tracking-widest">Daily Life</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              Life at HealthCentrix
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Every day brings new opportunities for joy, connection, and fulfillment
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-6">
              <div className="relative rounded-2xl overflow-hidden group">
                <img
                  src="https://images.unsplash.com/photo-1761634372910-016de07c85a8?w=500"
                  alt="Seniors playing cards"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f0518] via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h4 className="text-white font-semibold">Game Days</h4>
                  <p className="text-gray-300 text-sm">Cards, puzzles, and friendly competition</p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden group">
                <img
                  src="https://images.unsplash.com/photo-1773227060313-7623fd5bfa64?w=500"
                  alt="Staff caring for residents"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f0518] via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h4 className="text-white font-semibold">Personalized Care</h4>
                  <p className="text-gray-300 text-sm">Compassionate staff always ready to help</p>
                </div>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden group h-full min-h-[400px]">
              <img
                src="https://images.unsplash.com/photo-1758691031158-8d19bc43fcf8?w=600"
                alt="Seniors enjoying activities together"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f0518] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <h4 className="text-xl text-white font-semibold mb-2">Social Activities</h4>
                <p className="text-gray-300">Building friendships and creating memories together over coffee, puzzles, and shared stories</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="relative rounded-2xl overflow-hidden group">
                <img
                  src="https://images.unsplash.com/photo-1630595633877-9918ee257288?w=500"
                  alt="Spa and wellness"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f0518] via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h4 className="text-white font-semibold">Spa & Relaxation</h4>
                  <p className="text-gray-300 text-sm">Therapeutic treatments and pampering</p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden group">
                <img
                  src="https://images.unsplash.com/photo-1765896387398-1e1ae8d2eb85?w=500"
                  alt="Comfortable living spaces"
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f0518] via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h4 className="text-white font-semibold">Comfortable Spaces</h4>
                  <p className="text-gray-300 text-sm">Home-like environment with modern amenities</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Amenities</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
                State-of-the-Art Facility
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Our facility is equipped with the latest medical equipment and comfortable amenities to ensure the highest quality of care and living experience for all residents.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {amenities.map((amenity, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#00f0ff] shrink-0" />
                    <span className="text-gray-300 text-sm">{amenity}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="glass rounded-3xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">We Accept All Residents</h3>
                <div className="space-y-4">
                  {[
                    { title: "Private Pay", desc: "Flexible payment plans available" },
                    { title: "Medicaid", desc: "Accepted for qualifying residents" },
                    { title: "Medicare", desc: "Coverage for skilled nursing care" },
                    { title: "Long-Term Care Insurance", desc: "We work with all major providers" },
                    { title: "Hospice Care", desc: "Compassionate end-of-life services" },
                    { title: "Veterans Benefits", desc: "VA Aid & Attendance accepted" },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-[#0f0518] rounded-lg">
                      <Shield className="w-5 h-5 text-[#ff00ff] shrink-0 mt-0.5" />
                      <div>
                        <h4 className="text-white font-medium">{item.title}</h4>
                        <p className="text-gray-400 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Join */}
      <section className="py-20 lg:py-28 bg-[#1a0b2e]/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00f0ff] text-sm font-mono uppercase tracking-widest">Getting Started</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              How to Join Our Community
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We make the admission process simple and stress-free for families
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Contact Us", desc: "Call or fill out our inquiry form to speak with our admissions team", color: "#00f0ff" },
              { step: "02", title: "Schedule a Tour", desc: "Visit our facility, meet our staff, and see our amenities firsthand", color: "#ff00ff" },
              { step: "03", title: "Assessment", desc: "Our medical team evaluates care needs to create a personalized plan", color: "#ff9e00" },
              { step: "04", title: "Move In", desc: "We handle all the details to ensure a smooth transition to your new home", color: "#00f0ff" },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div 
                  className="text-6xl font-bold mb-4 opacity-30"
                  style={{ color: item.color }}
                >
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#ff9e00] text-sm font-mono uppercase tracking-widest">Testimonials</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
              What Families Say
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Hear from the families who have entrusted us with their loved ones' care
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                quote: "The staff at HealthCentrix treats my mother like family. The care she receives is exceptional, and the activities keep her engaged and happy. I couldn't ask for a better place for her.",
                name: "Jennifer Martinez",
                relation: "Daughter of Resident",
                rating: 5
              },
              {
                quote: "After my father's stroke, we were worried about finding quality rehabilitation care. HealthCentrix exceeded our expectations. The therapy team helped him regain his independence.",
                name: "Michael Thompson",
                relation: "Son of Resident",
                rating: 5
              },
              {
                quote: "The hospice care team showed such compassion during my grandmother's final days. They made her comfortable and supported our entire family through a difficult time.",
                name: "Sarah Williams",
                relation: "Granddaughter of Resident",
                rating: 5
              },
              {
                quote: "Movie nights, game days, and spa visits - my dad is living his best life! He's made so many friends and the staff knows him by name. This place is truly special.",
                name: "David Chen",
                relation: "Son of Resident",
                rating: 5
              },
              {
                quote: "The transition from hospital to HealthCentrix was seamless. The admissions team handled everything, and my mom received excellent care from day one.",
                name: "Patricia Johnson",
                relation: "Daughter of Resident",
                rating: 5
              },
              {
                quote: "Clean facilities, caring staff, and my father is actually enjoying his meals for the first time in years. The chef accommodates his dietary restrictions perfectly.",
                name: "Robert Garcia",
                relation: "Son of Resident",
                rating: 5
              }
            ].map((testimonial, index) => (
              <div
                key={index}
                data-testid={`testimonial-${index}`}
                className="glass rounded-2xl p-6 card-hover"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-[#ff9e00] fill-[#ff9e00]" />
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed mb-6 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="text-white font-semibold">{testimonial.name}</p>
                  <p className="text-gray-400 text-sm">{testimonial.relation}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="inquiry-form" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#ff00ff] text-sm font-mono uppercase tracking-widest">Get Started</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
                Schedule Your Visit Today
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Take the first step towards exceptional senior care. Our admissions team is ready to answer your questions and schedule a personal tour of our facility.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#00f0ff]/15 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-[#00f0ff]" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Call Us Directly</p>
                    <a href="tel:8449436175" className="text-white text-lg font-semibold hover:text-[#00f0ff]">
                      844-943-6175
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#ff00ff]/15 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#ff00ff]" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Admissions Hours</p>
                    <p className="text-white font-semibold">Mon - Sat: 9AM - 5PM</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-gray-400">
                <Star className="w-5 h-5 text-[#ff9e00]" />
                <Star className="w-5 h-5 text-[#ff9e00]" />
                <Star className="w-5 h-5 text-[#ff9e00]" />
                <Star className="w-5 h-5 text-[#ff9e00]" />
                <Star className="w-5 h-5 text-[#ff9e00]" />
                <span className="ml-2">Rated 5 stars by families</span>
              </div>
            </div>

            <div className="glass rounded-3xl p-8">
              <h3 className="text-xl font-bold text-white mb-6">Request Information</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className="text-gray-300 mb-2 block">Your Name *</Label>
                  <Input
                    name="name"
                    data-testid="senior-input-name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="John Doe"
                    className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Phone Number *</Label>
                  <Input
                    name="phone"
                    data-testid="senior-input-phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                    className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Email Address</Label>
                  <Input
                    name="email"
                    type="email"
                    data-testid="senior-input-email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Tell Us About Your Needs</Label>
                  <Textarea
                    name="message"
                    data-testid="senior-input-message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about the resident's care needs, timeline, or any questions..."
                    rows={4}
                    className="bg-[#2a1b3d] border-[#4b0082] text-white placeholder:text-gray-500"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="senior-submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full py-6 hover:bg-[#00c0cc] hover:shadow-[0_0_20px_rgba(0,240,255,0.6)] transition-all disabled:opacity-50"
                >
                  {isSubmitting ? "Submitting..." : "Request Information"}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </form>
              <p className="text-gray-500 text-xs text-center mt-4">
                We'll respond within 24 hours. Your information is kept confidential.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-gradient-to-r from-[#00f0ff]/10 to-[#ff00ff]/10 relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#00f0ff] to-[#ff00ff]" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Give Your Loved One the Care They Deserve
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            With 125 beds, 24/7 nursing care, and a commitment to dignity and comfort, 
            HealthCentrix is the right choice for your family.
          </p>
          <a href="tel:8449436175">
            <Button 
              data-testid="senior-cta-call"
              className="bg-[#00f0ff] text-black font-bold uppercase tracking-wider rounded-full px-10 py-6 text-lg hover:bg-[#00c0cc] hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] transition-all"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call Now: 844-943-6175
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
